// MainPage.js - the workspace shell: the sidebar (a drawer on phones - components/AppSidebar.js), the top bar, the demo
// banner, and the active page (pages/StudioWorkspace.js). It loads the signed-in person's profile for everything inside, and starts
// the welcome walkthrough on their first visit.
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMobileContext } from "../context/MobileContext";
import { ProfileContext, isOwner } from "../context/ProfileContext";
import { StudioTourProvider, useStudioTours } from "../reacttour/StudioTourContext";
import { getProfile } from "../api/studioAPI";
import { listPlans } from "../game-builder/api/gameBuilderAPI";
import analytics from "../services/analytics";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DemoOrgBanner from "../components/DemoOrgBanner";
import LoadingOverlay from "../components/common/LoadingOverlay";
import AppSidebar from "../components/AppSidebar";
import StudioWorkspace from "./StudioWorkspace";
import { TABS } from "./tabs";
import "../responsive.css";
import "./AppShell.css";

const TAB_KEYS = TABS.map((t) => t.key);
const labelOf = (key) => TABS.find((t) => t.key === key)?.label || "Dashboard";

function WelcomeTour() {
  const { profile } = React.useContext(ProfileContext);
  const { autoStart } = useStudioTours();
  useEffect(() => {
    if (!profile || (profile.tours_done || []).includes("welcome")) return undefined;
    let live = true;
    const timer = setTimeout(async () => {
      const hasPlans = await listPlans().then((d) => d.plans.length > 0).catch(() => false);
      if (live) autoStart("welcome", { hasPlans, isDemo: !!profile.org?.is_demo, isOwner: isOwner(profile) });
    }, 900);                                                      // give the page time to draw first
    return () => { live = false; clearTimeout(timer); };
  }, [profile, autoStart]);
  return null;
}

const MainPage = ({ token, logout }) => {
  const location = useLocation();
  const isMobile = useMobileContext();
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const segment = location.pathname.replace(/^\/app\/?/, "").split("/")[0];
  const activeTab = TAB_KEYS.includes(segment) ? segment : "dashboard";

  const refresh = useCallback(() => {
    getProfile().then((p) => { setProfile(p); setProfileError(""); }).catch((e) => setProfileError(e.message));
  }, []);
  useEffect(() => { refresh(); }, [refresh, token]);

  const setToursDone = useCallback((update) => {
    setProfile((p) => (p ? { ...p, tours_done: typeof update === "function" ? update(p.tours_done) : update } : p));
  }, []);

  useEffect(() => {
    analytics.pageView(`/app/${activeTab}`, `SimulateJob - ${labelOf(activeTab)}`);
  }, [activeTab]);

  if (!profile && !profileError) return <LoadingOverlay message="Loading your workspace..." />;

  return (
    <ProfileContext.Provider value={{ profile, refresh, setToursDone }}>
      <StudioTourProvider>
        <WelcomeTour />
        <div className="as-shell">
          {!isMobile && <AppSidebar activeTab={activeTab} />}
          {isMobile && <AppSidebar mobile open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} activeTab={activeTab} />}
          <div className="as-body">
            <Header logout={logout} onMenu={isMobile ? () => setMobileMenuOpen(true) : null} />
            <main className="as-main">
              {profileError && <p role="alert" style={{ color: "#b91c1c" }}>{profileError}</p>}
              <DemoOrgBanner logout={logout} />
              <StudioWorkspace activeTab={activeTab} token={token} logout={logout} />
            </main>
            <Footer />
          </div>
        </div>
      </StudioTourProvider>
    </ProfileContext.Provider>
  );
};

export default MainPage;
